import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { itemsApi, categoriesApi, locationsApi, statusesApi, conditionsApi, itemDetailsApi, type Item, type SearchParams } from '../lib/api'
import { itemSchema, itemUpdateSchema } from '../lib/schemas'

// Items queries
export function useItems(params: SearchParams = {}) {
  return useQuery({
    queryKey: ['items', params],
    queryFn: () => itemsApi.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useItem(id: string) {
  return useQuery({
    queryKey: ['item', id],
    queryFn: () => itemsApi.getById(id),
    enabled: !!id,
  })
}

// Items mutations
export function useCreateItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (item: Partial<Item>) => {
      // Validate with Zod
      const validatedItem = itemSchema.parse(item)
      return itemsApi.create(validatedItem)
    },
    onSuccess: () => {
      // Invalidate and refetch items
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export function useUpdateItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Item> }) => {
      // Use itemDetailsApi for detailed updates
      return itemDetailsApi.update(id, data)
    },
    onSuccess: (_, { id }) => {
      // Invalidate specific item and items list
      queryClient.invalidateQueries({ queryKey: ['item', id] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export function useDeleteItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: itemsApi.delete,
    onSuccess: () => {
      // Invalidate items list
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

// Categories queries
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.getAll,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Locations queries
export function useLocations() {
  return useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Statuses queries
export function useStatuses() {
  return useQuery({
    queryKey: ['statuses'],
    queryFn: statusesApi.getAll,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Conditions queries
export function useConditions() {
  return useQuery({
    queryKey: ['conditions'],
    queryFn: conditionsApi.getAll,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Item Details queries
export function useItemDetails(itemId: string) {
  return useQuery({
    queryKey: ['item', itemId],
    queryFn: () => itemDetailsApi.getById(itemId),
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

